import React from 'react';
import { MARK } from '../../../constants';

/**
 * Faturado e pago, na mesma língua em toda tela.
 *
 * O cartão do Kanban já dizia "⏳ A pagar" e "✅ Pago". A linha do Pipeline não
 * dizia nada: um serviço quitado descia a lista igual a um que nunca foi
 * faturado, porque a coluna Status só fala de ETAPA — e `StatusIndicator`
 * devolve nulo justamente quando está tudo em ordem, que é o caso de quem
 * acabou de pagar. Quem abre o Dashboard para saber de dinheiro lia a tabela
 * inteira sem achar a resposta.
 *
 * A COR É REDUNDANTE, NUNCA SOZINHA. A regra da casa é que estado se marca com
 * emoji, porque verde some no sol do campo, some no P&B da impressão e some
 * para quem não distingue verde de vermelho. O fundo verde claro da linha
 * quitada entrou por pedido do usuário e não revoga nada disso: ele vem POR
 * CIMA do ✅, que continua sendo quem carrega a informação. Tire a cor e a
 * linha continua legível; é esse o teste.
 *
 * O verde é só para o QUITADO. "A pagar" não ganha tom nenhum — pintar os dois
 * devolveria a colisão que tirou a cor daqui em primeiro lugar, e são sete
 * linhas contra uma.
 *
 * NADA quando o serviço ainda não foi faturado, e são a maioria — hoje 17 dos
 * 25. Marca que aparece em toda linha não informa; a ausência já diz "esse
 * ainda não chegou no dinheiro".
 */

/**
 * O fundo da linha quitada: verde bem claro, e um hover que não some.
 *
 * Mora aqui junto de `estadoDoDinheiro` porque é a mesma decisão vista de
 * outro ângulo — três telas pintam, e cada uma escolhendo o próprio tom seria
 * a divergência de sempre, agora em hexadecimal.
 */
export const FUNDO_QUITADO = 'bg-emerald-50 hover:bg-emerald-100';

/** Quitado é `paid`, que é espelho das parcelas — não um clique de alguém. */
export const estaQuitado = (service) => !!service?.paid;

export const estadoDoDinheiro = (service) => {
  if (!service) return null;

  const emReais = (v) => Number(v || 0).toLocaleString('pt-BR',
    { style: 'currency', currency: 'BRL' });
  const emData = (v) => (v ? new Date(v).toLocaleDateString('pt-BR') : null);

  if (service.paid) {
    const quando = emData(service.paid_date);
    return {
      mark: MARK.OK,
      texto: 'Pago',
      titulo: `Pago${service.billed_value ? `: ${emReais(service.billed_value)}` : ''}`
        + (quando ? ` em ${quando}` : ''),
    };
  }

  if (service.billed) {
    const quando = emData(service.billed_date);
    return {
      mark: MARK.PENDING,
      texto: 'A pagar',
      titulo: `Faturado${service.billed_value ? ` em ${emReais(service.billed_value)}` : ''}`
        + (quando ? `, em ${quando}` : '') + ' — ainda não recebido',
    };
  }

  return null;
};

/**
 * A etiqueta, onde o serviço aparece.
 *
 * `paid` é espelho mantido por gatilho a partir das parcelas (ver
 * `data/services.js`), então dizer "Pago" aqui é dizer o que a soma dos
 * recebimentos já decidiu — não um campo que alguém marcou à mão e esqueceu.
 */
const EstadoDoDinheiro = ({ service, className = '' }) => {
  const e = estadoDoDinheiro(service);
  if (!e) return null;
  return (
    <span
      title={e.titulo}
      className={`inline-block text-xs bg-slate-100 text-slate-600 rounded px-1 whitespace-nowrap ${className}`}
    >
      {e.mark} {e.texto}
    </span>
  );
};

export default EstadoDoDinheiro;
